import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/apiConfig';
import { useNotifications } from '../../context/NotificationContext';
import '../../styles/login.css';
import './ForgotPasswordPage.css';
import { logError } from '../../utils/logger';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const validateEmail = useCallback((emailValue) => {
    if (!emailValue.trim()) {
      return 'El correo es obligatorio';
    }
    if (!emailRegex.test(emailValue.trim())) {
      return 'Ingresa un correo válido';
    }
    return '';
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setError('');
      setSuccess(false);

      const emailError = validateEmail(email);
      if (emailError) {
        setError(emailError);
        return;
      }

      setLoading(true);

      try {
        const response = await fetch(buildApiUrl('/api/auth/forgot-password'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          setSuccess(true);
          notify({
            message: 'Si el email existe, recibirás un correo con instrucciones para recuperar tu contraseña.',
            type: 'success',
          });
        } else {
          const errorText = data?.error || 'Error al procesar la solicitud. Intenta nuevamente.';
          setError(errorText);
          notify({
            message: errorText,
            type: 'error',
          });
        }
      } catch (error) {
        logError('[ForgotPasswordPage] Error:', error);
        setError('Error de conexión. Intenta nuevamente.');
        notify({
          message: 'Error de conexión. Intenta nuevamente.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [email, notify, validateEmail],
  );

  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value);
    if (error) setError('');
  }, [error]);

  return (
    <div className="login-page forgot-password-page">
      <div className="login-container">
        <div className="login-form-section">
          <div className="login-form-header">
            <h2>Recuperar Contraseña</h2>
            <p>Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña</p>
          </div>

          {success ? (
            <div className="forgot-password-success">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>¡Correo enviado!</h3>
              <p>
                Si el email <strong>{email}</strong> existe en nuestro sistema, recibirás un correo con
                instrucciones para recuperar tu contraseña.
              </p>
              <p className="success-note">
                <i className="fas fa-info-circle"></i>
                Revisa tu bandeja de entrada y la carpeta de spam. El enlace expirará en 1 hora.
              </p>
              <div className="forgot-password-actions">
                <Link to="/login" className="btn-back-login">
                  <i className="fas fa-arrow-left"></i>
                  Volver al inicio de sesión
                </Link>
              </div>
            </div>
          ) : (
            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="forgotEmail" className="form-label">
                  <i className="fas fa-envelope" aria-hidden="true" />
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  id="forgotEmail"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  className={`form-input${error ? ' error' : ''}`}
                  placeholder="tu@email.com"
                  autoComplete="email"
                  required
                  autoFocus
                />
                {error ? <div className="form-error">{error}</div> : null}
              </div>

              <button type="submit" className="login-submit" disabled={loading}>
                <span
                  className="auth-loading"
                  aria-hidden="true"
                  style={{ display: loading ? 'inline-block' : 'none' }}
                />
                <i className="fas fa-paper-plane" aria-hidden="true" />
                {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
              </button>
            </form>
          )}

          <div className="register-link">
            <Link to="/login">
              <i className="fas fa-arrow-left"></i>
              Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

